import React, { useCallback, useRef, useState } from 'react';
import type { Attachment } from '../types';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { isAllowedAttachmentURL } from '../lib/attachment-url';

interface Props {
  attachments: Attachment[];
}

/**
 * Renders message attachments in a Discord-style grid.
 * - 1 image: full width (max 400px)
 * - 2 images: side by side
 * - 3-4 images: 2x2 grid
 * - Non-image files: download cards
 */
export function AttachmentGrid({ attachments }: Props) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const closeLightbox = useCallback(() => setLightboxUrl(null), []);
  useDismissibleLayer({
    active: lightboxUrl !== null,
    within: lightboxRef,
    onEscape: closeLightbox,
  });

  const safeAttachments = attachments.filter(a => isAllowedAttachmentURL(a.url));
  const images = safeAttachments.filter(a => a.content_type.startsWith('image/'));
  const files = safeAttachments.filter(a => !a.content_type.startsWith('image/'));

  const gridClass = images.length === 1
    ? 'attachment-grid attachment-grid-1'
    : images.length === 2
      ? 'attachment-grid attachment-grid-2'
      : 'attachment-grid attachment-grid-multi';

  return (
    <>
      {images.length > 0 && (
        <div className={gridClass}>
          {images.map((img) => (
            <button
              key={img.url}
              type="button"
              className="attachment-grid-item"
              aria-label={`Open image ${img.filename || 'attachment'}`}
              onClick={() => setLightboxUrl(img.url)}
            >
              <img
                src={img.url}
                alt={img.filename || 'attachment'}
                className="attachment-grid-img"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="attachment-files">
          {files.map((file) => (
            <a
              key={file.url}
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="attachment-file-card"
            >
              <span className="attachment-file-icon">📎</span>
              <span className="attachment-file-info">
                <span className="attachment-file-name">{file.filename}</span>
                {file.size_bytes && (
                  <span className="attachment-file-size">{formatBytes(file.size_bytes)}</span>
                )}
              </span>
            </a>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div ref={lightboxRef} className="attachment-lightbox" onClick={closeLightbox}>
          <img src={lightboxUrl} alt="full size" className="attachment-lightbox-img" />
        </div>
      )}
    </>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
